/**
 * SlideCategoryBadge
 *
 * A small coloured badge that labels a slide's AI-detected category. Rendered
 * as a `NodePill` in the `secondary` tone, so it inherits the brand pill
 * geometry and the 1 px outline; the per-category fill below overrides the
 * tone's default background through tailwind-merge.
 * Each category maps to one of the theme's `--chart-N` tokens so the
 * palette follows customer theming (DESIGN.md: "Use the 5 chart tokens
 * for data visualization. Do not invent new chart colors.").
 *
 * Mapping rationale:
 *   - title    → chart-1 (lead/identifier)
 *   - content  → chart-2 (body)
 *   - chart    → chart-3 (data viz — contextually fitting)
 *   - table    → chart-4 (structured)
 *   - image    → chart-5 (visual accent)
 *   - divider/closing/other → neutral muted (structural / unspecified)
 */

import { useTranslation } from "react-i18next"

import type { SlideCategory } from "@/features/slide-sense/types"

import { NodePill } from "@/components/ui/node-pill"
import { cn } from "@/lib/utils"

// ---------------------------------------------------------------------------
// Colour map — uses theme tokens so customer theming applies.
// We rely on Tailwind arbitrary values + the `chart-N` colour entries
// declared in `tailwind.config.js`, which already resolve to
// `hsl(var(--chart-N))`. The `/15` opacity gives the soft tint while
// `text-foreground` keeps WCAG AA contrast across all chart tokens —
// some chart hues (e.g. light yellow/orange) are too pale to render as
// text on a near-white tinted background. The coloured background
// already conveys the category; the text doesn't need to be tinted.
// ---------------------------------------------------------------------------

const categoryStyles: Record<SlideCategory, string> = {
  title: "bg-chart-1/15 text-foreground",
  content: "bg-chart-2/15 text-foreground",
  chart: "bg-chart-3/15 text-foreground",
  table: "bg-chart-4/15 text-foreground",
  image: "bg-chart-5/15 text-foreground",
  divider: "bg-muted text-muted-foreground",
  closing: "bg-muted text-muted-foreground",
  other: "bg-muted text-muted-foreground",
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface SlideCategoryBadgeProps {
  category: SlideCategory
  className?: string
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const categoryLabelKeys: Record<SlideCategory, string> = {
  title: "slideSense.categoryTitle",
  content: "slideSense.categoryContent",
  chart: "slideSense.categoryChart",
  table: "slideSense.categoryTable",
  image: "slideSense.categoryImage",
  divider: "slideSense.categoryDivider",
  closing: "slideSense.categoryClosing",
  other: "slideSense.categoryOther",
}

export function SlideCategoryBadge({ category, className }: SlideCategoryBadgeProps) {
  const { t } = useTranslation()

  return (
    <NodePill className={cn(categoryStyles[category], className)} size="sm" tone="secondary">
      {t(categoryLabelKeys[category])}
    </NodePill>
  )
}
